import { createId } from '../../utils/id'
import { toLocalCalendarDayKey } from '../../utils/dateNormalization'
import { farmTimeZone, farmToday } from '../../utils/farmTime'
import {
  listProductionUnitTableRows,
  upsertProductionUnitTableRowAndSync,
  type BiomassDataRow,
  type ProductionUnitTableContext
} from '../../utils/productionUnitTable'

// Crew Mode captures write lot-scoped event rows (MortalityLog,
// WeightSampleLog) that Biomass/Database overlay onto a same-(calendar day,
// lot) `ProductionRecord` row (mortalityLogBridge.ts / weightSampleLogBridge.ts
// key their rollup on exactly that pair). If nobody has added a row for
// today yet for this lot, the event has nothing to overlay onto and stays
// invisible there even though it's correctly stored — the bug the user hit
// 2026-09-16. This creates a bare row (no invented weight/mortality/stock
// values — Charter O-1/O-3, and matches how `ProductionRecord` rows are
// already row-identity-only per T3-A-07b) only when one doesn't already
// exist for (today, lot). Local-cache-only check + local-first write
// (`listProductionUnitTableRows` / `upsertProductionUnitTableRowAndSync`),
// so this never blocks on network (Charter O-5).
//
// Best-effort by design: callers fire this after a successful capture and
// swallow any rejection, mirroring the `syncPromise` pattern used elsewhere
// in the capture screens. A missing/failed row-ensure must never undo or
// block the capture itself.

// Findings #85: "today" is the farm's day (its time zone), not the phone's.
const todayDateSentinel = (farmId: string): { key: string; iso: string } => {
  const zone = farmTimeZone(farmId)
  const key = farmToday(zone)
  return { key, iso: `${key}T00:00:00Z` }
}

/**
 * Findings #88: a late entry ("Ayer" / "Hace 2 días") needs the row of ITS day,
 * not today's — otherwise, on a day nobody added a row for, the record stays
 * out of Base de datos, Biomasa, the Panel mortality widget and the row-based
 * Reportes. `dayKey` (YYYY-MM-DD, farm day) defaults to today.
 */
export const ensureTodayProductionRow = async (
  context: ProductionUnitTableContext,
  lot: { id: string; lotKey: string },
  dayKey?: string
): Promise<void> => {
  const lotKey = lot.lotKey.trim()
  if (!lotKey) return

  const today = todayDateSentinel(context.farmId)
  const todayKey = dayKey ?? today.key
  const todayIso = dayKey ? `${dayKey}T00:00:00Z` : today.iso
  const rows = await listProductionUnitTableRows(context)
  const hasRowForToday = rows.some((row) => (
    row.lot.trim() === lotKey && toLocalCalendarDayKey(row.date, farmTimeZone(context.farmId)) === todayKey
  ))
  if (hasRowForToday) return

  const newRow: BiomassDataRow = {
    id: createId('prod'),
    date: todayIso,
    lot: lotKey,
    lotId: lot.id,
    stage: 'Active',
    weight: '',
    dailyMortality: '',
    fishStock: '',
    health: '',
    healthDetail: ''
  }

  await upsertProductionUnitTableRowAndSync(context, newRow)
}
